import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowRight, Plus, Edit, Trash2, Save, X, FileText, Receipt } from 'lucide-react';
import { getWorkDetails, addWorkDetail, updateWorkDetail, deleteWorkDetail, getPriceItems } from '../api';
import { toast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WorkDetailList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [newRow, setNewRow] = useState({ partName: '', quantity: '', cost: '', unitPrice: '', supplierName: '', invoiceId: '' });
  const [editItem, setEditItem] = useState({ partName: '', quantity: '', cost: '', unitPrice: '', supplierName: '', invoiceId: '' });
  const [workId, setWorkId] = useState(null);
  const [workName, setWorkName] = useState('');
  const [showPriceList, setShowPriceList] = useState(false);
  const [priceItems, setPriceItems] = useState([]);
  const [loadingPriceList, setLoadingPriceList] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  useEffect(() => {
    if (location.state?.workId) {
      setWorkId(location.state.workId);
      setWorkName(location.state.workName || '');
      loadItems(location.state.workId);
      loadPriceList(location.state.workId);
      loadInvoices();
      loadSuppliers();
    } else {
      navigate('/work');
    }
  }, [location]);

  const loadSuppliers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/suppliers`);
      if (response.ok) {
        const data = await response.json();
        setSuppliers(data);
      }
    } catch (error) {
      console.error('Error loading suppliers:', error);
    }
  };

  const loadInvoices = async () => {
    try {
      const response = await fetch(`${API_URL}/api/purchase-invoices`);
      if (response.ok) {
        const data = await response.json();
        setInvoices(data);
      }
    } catch (error) {
      console.error('Error loading invoices:', error);
    }
  };

  const loadPriceList = async (wId) => {
    setLoadingPriceList(true);
    try {
      const data = await getPriceItems(wId);
      console.log('📋 Loaded price items:', data.length);
      setPriceItems(data);
    } catch (error) {
      console.error('❌ Error loading price list:', error);
      toast({
        title: 'שגיאה',
        description: 'לא ניתן לטעון מחירון',
        variant: 'destructive'
      });
    } finally {
      setLoadingPriceList(false);
    }
  };

  const loadItems = async (wId) => {
    try {
      const data = await getWorkDetails(wId);
      console.log('📋 Work details loaded:', data.length);
      setItems(data);
    } catch (error) {
      console.error('❌ Error loading work details:', error);
      toast({
        title: 'שגיאה',
        description: 'לא ניתן לטעון את פירוט העבודה',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewRowChange = (field, value) => {
    const updatedRow = { ...newRow, [field]: value };
    setNewRow(updatedRow);
  };

  const handleSaveNewRow = async () => {
    // Only save if required fields are filled
    if (newRow.partName && newRow.quantity && newRow.cost) {
      try {
        // Calculate customerPrice = (unitPrice * quantity) - (quantity * cost)
        const customerPrice = ((parseFloat(newRow.unitPrice) || 0) * (parseFloat(newRow.quantity) || 0)) - ((parseFloat(newRow.quantity) || 0) * (parseFloat(newRow.cost) || 0));
        const dataToSave = {
          ...newRow,
          customerPrice: customerPrice.toString()
        };
        const added = await addWorkDetail(workId, dataToSave);
        setItems([...items, added]);
        setNewRow({ partName: '', quantity: '', cost: '', unitPrice: '', supplierName: '', invoiceId: '' });
        toast({
          title: 'נשמר',
          description: 'הפריט נוסף בהצלחה'
        });
      } catch (error) {
        toast({
          title: 'שגיאה',
          description: 'לא ניתן להוסיף פריט',
          variant: 'destructive'
        });
      }
    }
  };

  const handleLastFieldBlur = () => {
    // Save only when leaving the last field (supplier name)
    setTimeout(() => {
      handleSaveNewRow();
    }, 100);
  };

  const handleSelectFromPriceList = (item) => {
    setNewRow({
      ...newRow,
      partName: item.partName,
      cost: item.price
    });
    setShowPriceList(false);
    toast({
      title: 'נבחר מהמחירון',
      description: `${item.partName} - ₪${item.price}`
    });
  };

  const handleOpenPriceList = async () => {
    // Reload price list when opening modal
    if (workId) {
      await loadPriceList(workId);
    }
    setShowPriceList(true);
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setEditItem({ 
      partName: item.partName, 
      quantity: item.quantity, 
      cost: item.cost, 
      unitPrice: item.unitPrice || '',
      supplierName: item.supplierName,
      invoiceId: item.invoiceId || '',
      workId: item.workId
    });
  };

  const handleUpdate = async (id) => {
    if (!editItem.partName || !editItem.quantity || !editItem.cost) {
      toast({
        title: 'שגיאה',
        description: 'נא למלא לפחות שם חלק, כמות ועלות',
        variant: 'destructive'
      });
      return;
    }

    try {
      // Calculate customerPrice = (unitPrice * quantity) - (quantity * cost)
      const customerPrice = ((parseFloat(editItem.unitPrice) || 0) * (parseFloat(editItem.quantity) || 0)) - ((parseFloat(editItem.quantity) || 0) * (parseFloat(editItem.cost) || 0));
      const dataToUpdate = {
        ...editItem,
        customerPrice: customerPrice.toString()
      };
      const updated = await updateWorkDetail(id, dataToUpdate);
      setItems(items.map(item => item.id === id ? updated : item));
      setEditingId(null);
      toast({
        title: 'הצלחה',
        description: 'הפריט עודכן בהצלחה'
      });
    } catch (error) {
      toast({
        title: 'שגיאה',
        description: 'לא ניתן לעדכן פריט',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('האם אתה בטוח שברצונך למחוק פריט זה?')) {
      try {
        await deleteWorkDetail(id);
        setItems(items.filter(item => item.id !== id));
        toast({
          title: 'הצלחה',
          description: 'הפריט נמחק בהצלחה'
        });
      } catch (error) {
        toast({
          title: 'שגיאה',
          description: 'לא ניתן למחוק פריט',
          variant: 'destructive'
        });
      }
    }
  };

  return (
    <>
      {/* Price List Modal */}
      {showPriceList && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowPriceList(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-800">בחר מהמחירון</h2>
                <Button onClick={() => setShowPriceList(false)} variant="outline" size="sm">
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="max-h-96 overflow-y-auto">
                {loadingPriceList ? (
                  <div className="text-center py-8 text-gray-600">
                    <p>טוען מחירון...</p>
                  </div>
                ) : priceItems.length === 0 ? (
                  <div className="text-center py-8 text-gray-600">
                    <p>אין פריטים במחירון</p>
                    <p className="text-sm mt-2">הוסף פריטים בדף המחירון</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {priceItems.map((item) => (
                      <div
                        key={item.id}
                        onClick={() => handleSelectFromPriceList(item)}
                        className="flex justify-between items-center p-4 border rounded-lg hover:bg-orange-50 cursor-pointer transition-colors"
                      >
                        <span className="font-semibold text-gray-800">{item.partName}</span>
                        <span className="text-lg text-orange-600 font-bold">₪{item.price}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50" dir="rtl">
        <div className="container mx-auto px-4 py-16">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-800">פירוט עבודה</h1>
            {workName && <p className="text-lg text-gray-600 mt-2">{workName}</p>}
          </div>
          <Button
            onClick={() => navigate(-1)}
            variant="outline"
            className="border-2"
          >
            <ArrowRight className="ml-2 w-5 h-5" />
            חזרה
          </Button>
        </div>

        {/* Content */}
        <div className="max-w-full mx-auto">
          <Card className="shadow-xl">
            <CardHeader className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white">
              <CardTitle className="text-2xl">פירוט מלא ({items.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {isLoading ? (
                <div className="p-8 text-center text-gray-600">
                  <p className="text-lg">טוען...</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-4 text-right text-sm font-bold text-gray-700">שם חלק</th>
                        <th className="px-4 py-4 text-right text-sm font-bold text-gray-700">כמות</th>
                        <th className="px-4 py-4 text-right text-sm font-bold text-gray-700">עלות</th>
                        <th className="px-4 py-4 text-right text-sm font-bold text-gray-700 bg-green-50">מחיר ללקוח</th>
                        <th className="px-4 py-4 text-right text-sm font-bold text-gray-700 bg-yellow-50">סכום</th>
                        <th className="px-4 py-4 text-right text-sm font-bold text-gray-700 bg-blue-50">רווח</th>
                        <th className="px-4 py-4 text-right text-sm font-bold text-gray-700">שם ספק</th>
                        <th className="px-4 py-4 text-right text-sm font-bold text-gray-700 bg-purple-50">מספר חשבונית</th>
                        <th className="px-4 py-4 text-center text-sm font-bold text-gray-700">פעולות</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {items.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                          {editingId === item.id ? (
                            <>
                              <td className="px-4 py-4">
                                <Input
                                  value={editItem.partName}
                                  onChange={(e) => setEditItem({ ...editItem, partName: e.target.value })}
                                />
                              </td>
                              <td className="px-4 py-4">
                                <Input
                                  value={editItem.quantity}
                                  onChange={(e) => setEditItem({ ...editItem, quantity: e.target.value })}
                                  type="number"
                                  className="[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                  style={{ MozAppearance: 'textfield' }}
                                />
                              </td>
                              <td className="px-4 py-4">
                                <Input
                                  value={editItem.cost}
                                  onChange={(e) => setEditItem({ ...editItem, cost: e.target.value })}
                                  type="number"
                                  className="[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                  style={{ MozAppearance: 'textfield' }}
                                />
                              </td>
                              <td className="px-4 py-4 bg-green-50">
                                <Input
                                  value={editItem.unitPrice}
                                  onChange={(e) => setEditItem({ ...editItem, unitPrice: e.target.value })}
                                  type="number"
                                  placeholder="מחיר ללקוח"
                                  className="[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                  style={{ MozAppearance: 'textfield' }}
                                />
                              </td>
                              <td className="px-4 py-4 bg-yellow-50">
                                <span className="font-bold text-yellow-700">
                                  ₪{(parseFloat(editItem.quantity) || 0) * (parseFloat(editItem.cost) || 0)}
                                </span>
                              </td>
                              <td className="px-4 py-4 bg-blue-50">
                                <span className="font-bold text-blue-700">
                                  ₪{((parseFloat(editItem.unitPrice) || 0) * (parseFloat(editItem.quantity) || 0)) - ((parseFloat(editItem.quantity) || 0) * (parseFloat(editItem.cost) || 0))}
                                </span>
                              </td>
                              <td className="px-4 py-4">
                                <select
                                  value={editItem.supplierName || ''}
                                  onChange={(e) => setEditItem({ ...editItem, supplierName: e.target.value })}
                                  className="w-full px-2 py-1 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                >
                                  <option value="">בחר ספק</option>
                                  {suppliers.map(sup => (
                                    <option key={sup.id} value={sup.name}>
                                      {sup.name}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-4 py-4 bg-purple-50">
                                <select
                                  value={editItem.invoiceId || ''}
                                  onChange={(e) => setEditItem({ ...editItem, invoiceId: e.target.value })}
                                  className="w-full px-2 py-1 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                                >
                                  <option value="">בחר חשבונית</option>
                                  {invoices.map(inv => (
                                    <option key={inv.id} value={inv.id}>
                                      {inv.invoiceNumber || inv.fileName}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-4 py-4">
                                <div className="flex gap-2 justify-center">
                                  <Button onClick={() => handleUpdate(item.id)} size="sm" className="bg-green-500 hover:bg-green-600">
                                    <Save className="w-4 h-4" />
                                  </Button>
                                  <Button onClick={() => setEditingId(null)} size="sm" variant="outline">
                                    <X className="w-4 h-4" />
                                  </Button>
                                </div>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="px-4 py-4 text-gray-800 font-medium">{item.partName}</td>
                              <td className="px-4 py-4 text-gray-800">{item.quantity}</td>
                              <td className="px-4 py-4 text-gray-800">₪{item.cost}</td>
                              <td className="px-4 py-4 bg-green-50 text-green-700 font-medium">
                                {item.unitPrice ? `₪${item.unitPrice}` : '-'}
                              </td>
                              <td className="px-4 py-4 bg-yellow-50">
                                <span className="font-bold text-yellow-700">
                                  ₪{(parseFloat(item.quantity) || 0) * (parseFloat(item.cost) || 0)}
                                </span>
                              </td>
                              <td className="px-4 py-4 bg-blue-50">
                                <span className="font-bold text-blue-700">
                                  ₪{((parseFloat(item.unitPrice) || 0) * (parseFloat(item.quantity) || 0)) - ((parseFloat(item.quantity) || 0) * (parseFloat(item.cost) || 0))}
                                </span>
                              </td>
                              <td className="px-4 py-4 text-gray-800">{item.supplierName || '-'}</td>
                              <td className="px-4 py-4 bg-purple-50">
                                {item.invoiceId ? (
                                  <span className="flex items-center gap-1 text-purple-700">
                                    <Receipt className="w-4 h-4" />
                                    {invoices.find(inv => inv.id === item.invoiceId)?.invoiceNumber || 'חשבונית'}
                                  </span>
                                ) : '-'}
                              </td>
                              <td className="px-4 py-4">
                                <div className="flex gap-2 justify-center">
                                  <Button onClick={() => handleEdit(item)} size="sm" variant="outline">
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button onClick={() => handleDelete(item.id)} size="sm" variant="outline" className="text-red-600 hover:bg-red-50">
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                      
                      {/* New Row - Always visible at bottom */}
                      <tr className="bg-green-50 hover:bg-green-100 transition-colors">
                        <td className="px-4 py-4">
                          <div className="flex gap-2">
                            <Input
                              value={newRow.partName}
                              onChange={(e) => handleNewRowChange('partName', e.target.value)}
                              placeholder="שם חלק..."
                              className="border-green-300 focus:border-green-500"
                            />
                            <Button
                              type="button"
                              onClick={handleOpenPriceList}
                              variant="outline"
                              size="sm"
                              className="border-orange-500 text-orange-600 hover:bg-orange-50"
                            >
                              <FileText className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <Input
                            value={newRow.quantity}
                            onChange={(e) => handleNewRowChange('quantity', e.target.value)}
                            placeholder="כמות..."
                            type="number"
                            className="border-green-300 focus:border-green-500 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                            style={{ MozAppearance: 'textfield' }}
                          />
                        </td>
                        <td className="px-4 py-4">
                          <Input
                            value={newRow.cost}
                            onChange={(e) => handleNewRowChange('cost', e.target.value)}
                            placeholder="עלות..."
                            type="number"
                            className="border-green-300 focus:border-green-500 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                            style={{ MozAppearance: 'textfield' }}
                          />
                        </td>
                        <td className="px-4 py-4 bg-green-100">
                          <Input
                            value={newRow.unitPrice}
                            onChange={(e) => handleNewRowChange('unitPrice', e.target.value)}
                            placeholder="מחיר ללקוח..."
                            type="number"
                            className="border-green-300 focus:border-green-500 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                            style={{ MozAppearance: 'textfield' }}
                          />
                        </td>
                        <td className="px-4 py-4 bg-yellow-100">
                          <span className="font-bold text-yellow-700">
                            ₪{(parseFloat(newRow.quantity) || 0) * (parseFloat(newRow.cost) || 0)}
                          </span>
                        </td>
                        <td className="px-4 py-4 bg-blue-100">
                          <span className="font-bold text-blue-700">
                            ₪{((parseFloat(newRow.unitPrice) || 0) * (parseFloat(newRow.quantity) || 0)) - ((parseFloat(newRow.quantity) || 0) * (parseFloat(newRow.cost) || 0))}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <select
                            value={newRow.supplierName || ''}
                            onChange={(e) => handleNewRowChange('supplierName', e.target.value)}
                            className="w-full px-2 py-2 border border-green-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                          >
                            <option value="">בחר ספק</option>
                            {suppliers.map(sup => (
                              <option key={sup.id} value={sup.name}>
                                {sup.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-4 bg-purple-100">
                          <select
                            value={newRow.invoiceId || ''}
                            onChange={(e) => handleNewRowChange('invoiceId', e.target.value)}
                            onBlur={handleLastFieldBlur}
                            className="w-full px-2 py-2 border border-green-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                          >
                            <option value="">בחר חשבונית</option>
                            {invoices.map(inv => (
                              <option key={inv.id} value={inv.id}>
                                {inv.invoiceNumber || inv.fileName}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <Button
                            onClick={handleSaveNewRow}
                            size="sm"
                            className="bg-green-500 hover:bg-green-600 text-white"
                          >
                            <Save className="w-4 h-4 ml-1" />
                            שמור
                          </Button>
                        </td>
                      </tr>
                    </tbody>
                    {/* Total Rows */}
                    <tfoot className="border-t-4 border-yellow-400">
                      {/* סה"כ סכום */}
                      <tr className="bg-gradient-to-r from-yellow-100 to-orange-100">
                        <td colSpan="3" className="px-4 py-3 text-right">
                          <span className="text-lg font-bold text-gray-800">סה"כ עלות:</span>
                        </td>
                        <td colSpan="1"></td>
                        <td className="px-4 py-3 bg-yellow-200">
                          <span className="text-xl font-bold text-yellow-800">
                            ₪{items.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.cost) || 0), 0).toLocaleString()}
                          </span>
                        </td>
                        <td colSpan="4"></td>
                      </tr>
                      {/* סה"כ מחיר ללקוח */}
                      <tr className="bg-gradient-to-r from-blue-100 to-indigo-100">
                        <td colSpan="3" className="px-4 py-3 text-right">
                          <span className="text-lg font-bold text-gray-800">סה"כ מחיר ללקוח:</span>
                        </td>
                        <td className="px-4 py-3 bg-green-200">
                          <span className="text-xl font-bold text-green-800">
                            ₪{items.reduce((sum, item) => sum + (parseFloat(item.unitPrice) || 0) * (parseFloat(item.quantity) || 0), 0).toLocaleString()}
                          </span>
                        </td>
                        <td colSpan="5"></td>
                      </tr>
                      {/* סה"כ רווח */}
                      <tr className="bg-gradient-to-r from-green-200 to-emerald-200">
                        <td colSpan="5" className="px-4 py-4 text-right">
                          <span className="text-xl font-bold text-gray-800">💰 סה"כ רווח:</span>
                        </td>
                        <td className="px-4 py-4 bg-green-300" colSpan="3">
                          <span className="text-2xl font-bold text-green-800">
                            ₪{items.reduce((sum, item) => sum + (((parseFloat(item.unitPrice) || 0) * (parseFloat(item.quantity) || 0)) - ((parseFloat(item.quantity) || 0) * (parseFloat(item.cost) || 0))), 0).toLocaleString()}
                          </span>
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
    </>
  );
};

export default WorkDetailList;
